import React from 'react';
import { LOCAL_CATEGORY_ICONS } from '../../assets/categoryIcons';

export type AppPreviewBrand = {
  primary?: string;
  secondary?: string;
  tertiary?: string;
  quaternary?: string;
  logo?: string;
  orgName?: string;
  tagLine?: string;
  description?: string;
  banner?: string;
  partnerId?: string | number | null;
};

const PHONE_W = 260;
const PHONE_H = 540;

/** Preserve white / none / url fills used as SVG backgrounds */
const SKIP_PAINT =
  'none|url\\(|currentColor|white|#fff|#ffffff|#FFF|#FFFFFF';

function colorizeCategorySvg(xml: string, color: string): string {
  const skip = SKIP_PAINT;
  return xml
    .replace(/currentColor/gi, color)
    .replace(
      new RegExp(`(fill)="(?!${skip})[^"]*"`, 'gi'),
      `$1="${color}"`
    )
    .replace(
      new RegExp(`(fill)='(?!${skip})[^']*'`, 'gi'),
      `$1='${color}'`
    )
    .replace(
      new RegExp(`(stroke)="(?!${skip})[^"]*"`, 'gi'),
      `$1="${color}"`
    )
    .replace(
      new RegExp(`(stroke)='(?!${skip})[^']*'`, 'gi'),
      `$1='${color}'`
    )
    .replace(
      new RegExp(`(fill)\\s*:\\s*(?!${skip})[^;"}]+`, 'gi'),
      `$1:${color}`
    )
    .replace(
      new RegExp(`(stroke)\\s*:\\s*(?!${skip})[^;"}]+`, 'gi'),
      `$1:${color}`
    );
}

/** iPhone-style chrome around the app screen */
export const PhoneFrame: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div
    className='relative bg-black rounded-[28px] p-[10px] shadow-xl'
    style={{ width: PHONE_W, height: PHONE_H }}
  >
    <div className='absolute -left-[2px] top-[80px] w-[3px] h-[28px] bg-gray-700 rounded-l' />
    <div className='absolute -left-[2px] top-[120px] w-[3px] h-[48px] bg-gray-700 rounded-l' />
    <div className='absolute -right-[2px] top-[110px] w-[3px] h-[64px] bg-gray-700 rounded-r' />

    <div className='w-full h-full bg-white rounded-[20px] overflow-hidden relative flex flex-col'>
      <div className='absolute top-0 left-0 right-0 z-20 h-[28px] flex items-center justify-between px-4 text-[10px] font-semibold text-black pointer-events-none'>
        <span>9:41</span>
        <div className='absolute left-1/2 -translate-x-1/2 top-[6px] w-[72px] h-[18px] bg-black rounded-full' />
        <div className='flex items-center gap-[3px]'>
          <svg width='12' height='10' viewBox='0 0 16 12' fill='black'>
            <rect x='0' y='6' width='3' height='6' rx='0.5' />
            <rect x='4.5' y='4' width='3' height='8' rx='0.5' />
            <rect x='9' y='2' width='3' height='10' rx='0.5' />
            <rect x='13.5' y='0' width='2.5' height='12' rx='0.5' />
          </svg>
          <svg width='14' height='10' viewBox='0 0 24 12' fill='none'>
            <rect
              x='0.5'
              y='0.5'
              width='20'
              height='11'
              rx='2'
              stroke='black'
            />
            <rect x='2' y='2' width='15' height='8' rx='1' fill='black' />
            <path d='M22 4v4a1.5 1.5 0 000-4z' fill='black' />
          </svg>
        </div>
      </div>
      <div className='pt-[28px] flex-1 flex flex-col min-h-0 overflow-hidden'>
        {children}
      </div>
      <div className='absolute bottom-1 left-0 right-0 flex justify-center z-20 pointer-events-none'>
        <div className='w-[80px] h-[4px] bg-black/25 rounded-full' />
      </div>
    </div>
  </div>
);

const SearchIcon = ({ color }: { color: string }) => (
  <svg width='16' height='16' viewBox='0 0 27 27' fill='none'>
    <path
      d='M21.321 18.182c-.317-.317-.74-.317-1.056 0l-2.22-2.223c2.96-3.917 2.642-9.528-.951-13.021a10.002 10.002 0 00-14.161 0c-3.91 3.917-3.91 10.269 0 14.186 3.593 3.599 9.088 3.917 12.998.952l2.22 2.224c-.317.317-.317.74 0 1.058l5.073 5.082a.806.806 0 00.528.211.806.806 0 00.528-.211l2.114-2.118c.317-.317.317-.74 0-1.058l-5.073-5.082zm-7.609-4.552a5.16 5.16 0 01-7.292 0 5.182 5.182 0 010-7.305 5.16 5.16 0 017.292 0 5.182 5.182 0 010 7.305z'
      fill={color}
    />
  </svg>
);

/** clo-app ExploreCategoryIcon is 35×35 on ~390pt; scaled for 260px preview */
const CAT_ICON_PX = 24;

const CategoryTileIcon: React.FC<{
  svgXml: string;
  color: string;
}> = ({ svgXml, color }) => (
  <div
    className='flex items-center justify-center [&>svg]:w-full [&>svg]:h-full'
    style={{ width: CAT_ICON_PX, height: CAT_ICON_PX }}
    dangerouslySetInnerHTML={{
      __html: colorizeCategorySvg(svgXml, color),
    }}
  />
);

/** Landing = clo-app OnboardingScreen slide 0 */
export const LandingPreview: React.FC<AppPreviewBrand> = ({
  primary = '#1133BB',
  quaternary = '#00C389',
  logo = '',
  orgName = '',
  tagLine = '',
  description = '',
}) => (
  <PhoneFrame>
    <div className='flex-1 flex flex-col min-h-0 bg-white'>
      <div
        className='relative flex flex-col items-center justify-center text-white text-center px-3'
        style={{ backgroundColor: primary, flex: '0 0 46%' }}
      >
        <p className='text-[15px] font-semibold leading-none'>Welcome to</p>
        <p className='text-[15px] font-bold leading-tight mt-1'>
          {orgName || '[ADD PROGRAM NAME]'}
        </p>
        <p className='text-[11px] mt-4 px-2 leading-snug font-medium opacity-95'>
          {tagLine || '[ADD TAG LINE]'}
        </p>
        {/* clo-app OnboardingScreen: height 100, Q mid→y=2 — scaled for preview */}
        <svg
          className='absolute bottom-0 left-0 w-full pointer-events-none'
          style={{ height: 58, marginBottom: -1 }}
          viewBox='0 0 280 100'
          preserveAspectRatio='none'
        >
          <path
            d='M0 100 Q140 2 280 100 L280 100 L0 100 Z'
            fill='#fff'
          />
        </svg>
      </div>

      <div className='flex-1 flex flex-col items-center px-4 pt-1 pb-3 bg-white min-h-0'>
        <div className='h-[68px] w-full flex items-center justify-center'>
          {logo ? (
            <img
              src={logo}
              alt=''
              className='max-h-[68px] max-w-[170px] object-contain'
            />
          ) : (
            <span className='text-xs font-semibold' style={{ color: primary }}>
              [ADD LOGO]
            </span>
          )}
        </div>
        <p className='text-[11px] text-center text-black leading-[17px] mt-5 px-1 flex-1 overflow-hidden'>
          {description || '[ADD DESCRIPTION]'}
        </p>
        <button
          type='button'
          className='w-full text-white text-[12px] font-semibold py-[8px] rounded-lg mt-2 leading-none'
          style={{ backgroundColor: quaternary }}
        >
          Visit Website
        </button>
      </div>
    </div>
  </PhoneFrame>
);

/** Explore = clo-app ExploreScreen (guest) + Hub FAB */
export const ExplorePreview: React.FC<AppPreviewBrand> = ({
  primary = '#1133BB',
  tertiary = '#1133BB',
  quaternary = '#00C389',
  logo = '',
  banner = '',
}) => {
  const iconColor = tertiary || primary;

  return (
    <PhoneFrame>
      <div className='flex-1 flex flex-col min-h-0 bg-[#F5F5F5] relative'>
        <div className='bg-white px-3.5 flex items-center h-[44px] shrink-0'>
          {logo ? (
            <img
              src={logo}
              alt=''
              className='h-[34px] max-w-[150px] object-contain object-left'
            />
          ) : (
            <span
              className='text-[11px] font-semibold'
              style={{ color: primary }}
            >
              [ADD LOGO]
            </span>
          )}
        </div>

        <div className='bg-white px-3 pt-1.5 pb-1 flex items-center justify-between shrink-0 gap-1'>
          <span
            className='text-[11px] font-bold leading-none whitespace-nowrap truncate min-w-0'
            style={{ color: primary }}
          >
            Sign up / Sign in to get started
          </span>
          <span
            className='w-[28px] h-[28px] rounded-full flex items-center justify-center shrink-0 ml-2'
            style={{ backgroundColor: quaternary }}
          >
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none'>
              <path
                d='M5 12h12M13 6l6 6-6 6'
                stroke='#fff'
                strokeWidth='2.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </span>
        </div>

        <div className='bg-white px-3 pb-2 shrink-0'>
          <div className='bg-[#EDEDED] rounded h-[34px] flex items-center px-2.5 gap-2'>
            <SearchIcon color={quaternary} />
            <span className='text-[12px] text-[#444]'>Search for anything</span>
          </div>
        </div>

        <div
          className='w-full shrink-0 relative'
          style={{ height: 112, backgroundColor: primary }}
        >
          {banner ? (
            <img src={banner} alt='' className='w-full h-full object-cover' />
          ) : (
            <div className='w-full h-full flex items-center justify-center text-white/70 text-[10px]'>
              [ADD BANNER]
            </div>
          )}
          <div className='absolute bottom-2.5 inset-x-0 flex justify-center gap-[5px]'>
            <span className='w-[7px] h-[7px] rounded-full bg-gray-500' />
            <span className='w-[7px] h-[7px] rounded-full bg-gray-100' />
            <span className='w-[7px] h-[7px] rounded-full bg-gray-500' />
          </div>
        </div>

        {/* clo-app: rounded-2xl m-[5px] py-2, icon 35 — scaled for 260px phone */}
        <div className='flex-1 overflow-hidden px-[4px] pt-[4px] pb-12 grid grid-cols-2 gap-[4px] content-start bg-[#F5F5F5]'>
          {LOCAL_CATEGORY_ICONS.slice(0, 8).map(cat => (
            <div
              key={cat.name}
              className='bg-white rounded-xl flex flex-col items-center justify-center py-1.5 px-0.5 relative'
            >
              {cat.isNew && (
                <span className='absolute top-1 right-1 bg-[#00C389] text-white text-[7px] font-bold px-1 py-[1px] rounded-full leading-none'>
                  New
                </span>
              )}
              <CategoryTileIcon svgXml={cat.svgXml} color={iconColor} />
              <span
                className='text-[9px] font-semibold text-center mt-1 leading-tight px-0.5'
                style={{ color: iconColor }}
              >
                {cat.name}
              </span>
            </div>
          ))}
        </div>

        {/* clo-app HubToggleFab: 56×56, MenuIcon 28 — scaled ≈ 37 / 18 */}
        <div className='absolute bottom-[12px] inset-x-0 flex justify-center z-10 pointer-events-none'>
          <div
            className='w-[37px] h-[37px] rounded-full flex items-center justify-center'
            style={{
              backgroundColor: primary,
              boxShadow: '0 2px 5px rgba(0,0,0,0.25)',
            }}
          >
            <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
              <path
                d='M4 7h16M4 12h16M4 17h16'
                stroke='#fff'
                strokeWidth='2.4'
                strokeLinecap='round'
              />
            </svg>
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
};

/** Side-by-side pair used on Program Branding */
export const AppPreviewPair: React.FC<{
  brand: AppPreviewBrand;
  loading?: boolean;
}> = ({ brand, loading }) => {
  if (loading) {
    return (
      <>
        <div className='bg-white animate-pulse rounded-[28px] h-[540px] w-[260px]' />
        <div className='bg-white animate-pulse rounded-[28px] h-[540px] w-[260px]' />
      </>
    );
  }

  return (
    <>
      <LandingPreview {...brand} />
      <ExplorePreview {...brand} />
    </>
  );
};

export default AppPreviewPair;
